/**
 * `/workspaces/:workspaceId/runs/:runId/report` — wraps the Playwright
 * HTML report in an iframe so it inherits the ottotester chrome (top
 * nav + left sidebar) instead of opening a barren standalone page.
 *
 * The iframe loads `/api/runs/:runId/report/index.html` (same origin,
 * session-cookie authenticated). The report's internal SPA routing,
 * sub-resource requests, and trace-viewer link all stay inside the
 * iframe — one consistent chrome end-to-end. No "open in new tab"
 * escape hatch by design: the report should never appear standalone.
 *
 * Same-origin assumption. Theme sync, CSS injection, and the
 * target="_blank" click interceptor all require `contentDocument`
 * access, which the browser only grants for same-origin frames.
 * `reportUrl()` (apps/frontend/src/lib/api.ts) is hard-coded to a
 * relative `/api/...` path for exactly that reason — production
 * deploys are single-origin, and Vite's dev proxy keeps `/api`
 * same-origin in local dev. An explicit cross-origin
 * `VITE_API_URL` override would still break the wrap, so the
 * report URL bypasses that override on purpose.
 */
import { useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button, Badge } from '@/components/ui'
import { DownloadPdfButton } from '@/components/DownloadPdfButton'
import { EmailPdfButton } from '@/components/EmailPdfButton'
import { reportUrl } from '@/lib/api'
import { useTestRun } from './dashboard/hooks/useTestRun'
import { useTestSuite } from './dashboard/hooks/useTestSuite'
import { formatDateTime, formatDuration } from '@/lib/utils'

export default function RunReport() {
  const { runId } = useParams<{ workspaceId: string; runId: string }>()
  const navigate = useNavigate()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const innerObsRef = useRef<MutationObserver | null>(null)
  // Header data — mirrors the run-detail page's header so the user
  // doesn't lose suite / type / timing context when drilling into the
  // detailed (Playwright HTML) report. Suite name resolves only when
  // the run has a `testSuiteId` (discovery / legacy runs fall back to
  // a label). Both queries are TanStack-cached so navigating in and
  // out of this page doesn't re-fetch.
  const { data: run, isLoading: runLoading } = useTestRun(runId)
  const { data: suite, isLoading: suiteLoading } = useTestSuite(run?.testSuiteId)
  let suiteLabel: string
  if (!run && runLoading) suiteLabel = 'Loading…'
  else if (suiteLoading) suiteLabel = 'Loading suite…'
  else if (suite?.name) suiteLabel = suite.name
  // A run with `testSuiteId` set but no resolvable suite name means
  // the suite was deleted / lookup failed — surface that explicitly.
  // A run with NO `testSuiteId` is a plain replay (ad-hoc or AI-mode)
  // that never had a suite — "suite unknown" would mislead.
  else if (run?.testSuiteId) suiteLabel = 'replay run · suite unknown'
  else suiteLabel = 'replay run'

  // Compute desired PW theme class from ottotester's `<html class="dark">`
  // (set by useTheme.ts) and apply it to the iframe's `<html>` only when
  // it doesn't already match — that no-op short-circuit keeps the inner
  // MutationObserver loop-safe.
  const syncIframeTheme = () => {
    const doc = iframeRef.current?.contentDocument
    if (!doc?.documentElement) return
    const want = window.document.documentElement.classList.contains('dark')
      ? 'dark-mode'
      : 'light-mode'
    const cl = doc.documentElement.classList
    const have = cl.contains('dark-mode')
      ? 'dark-mode'
      : cl.contains('light-mode')
        ? 'light-mode'
        : null
    if (have === want) return
    cl.remove('light-mode', 'dark-mode')
    cl.add(want)
  }

  // React to ottotester theme toggles while the report is open. The
  // parent's `<html class>` changes whenever the user flips
  // light/dark/system — we mirror that into the iframe immediately
  // instead of waiting for the next iframe reload.
  useEffect(() => {
    const observer = new MutationObserver(syncIframeTheme)
    observer.observe(window.document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => {
      observer.disconnect()
      innerObsRef.current?.disconnect()
    }
  }, [])

  if (!runId) {
    return (
      <div className="text-sm text-muted-foreground">Run not found.</div>
    )
  }

  const src = reportUrl(runId)

  // Post-load adjustments to the iframe document:
  //
  //   (1) Theme sync. PW's report uses `light-mode` / `dark-mode`
  //       classes on `:root` (it stores the user's pick in
  //       localStorage and applies the class on its own bootstrap).
  //       Mirror ottotester's current theme — read by checking the
  //       parent `<html>` for the `dark` class that
  //       useTheme.ts applies — so the report visually matches the
  //       surrounding chrome.
  //
  //   (2) Hide the report's settings gear. PW renders it as
  //       `<div role="button" class="subnav-item" title="Settings">`;
  //       since the parent app drives the theme now, the gear is
  //       redundant and visually inconsistent with our chrome.
  //
  //   (3) Capture-phase click interceptor on `target="_blank"`
  //       anchors. The trace-viewer link inside the report uses
  //       this; rewriting the click to navigate the iframe itself
  //       keeps traces accessible without ever escaping into a
  //       barren standalone tab. The sandbox attribute is the
  //       defense-in-depth backstop — omitting `allow-popups`
  //       neutralises any window.open / target="_blank" that
  //       slips past the listener.
  //
  // Capture phase + event delegation means we don't need a
  // MutationObserver — links rendered later by the report's SPA
  // still fire through the document's click listener.
  const onLoad = () => {
    const doc = iframeRef.current?.contentDocument
    if (!doc) return

    syncIframeTheme()

    // PW's React effects re-apply their own theme class on first
    // mount of the settings popup, and on any state change inside
    // the report. Watch the iframe `<html>` so any class write that
    // disagrees with ottotester's theme is corrected within a
    // microtask. Disconnect+replace on every iframe load so we
    // don't leak observers if the report navigates internally and
    // a fresh `load` fires.
    innerObsRef.current?.disconnect()
    const innerObs = new MutationObserver(syncIframeTheme)
    innerObs.observe(doc.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    innerObsRef.current = innerObs

    const style = doc.createElement('style')
    style.textContent = `
      [title="Settings"] { display: none !important; }
      [data-testid="settings-dialog"] { display: none !important; }
    `
    doc.head.appendChild(style)

    doc.addEventListener(
      'click',
      (e) => {
        const a = (e.target as Element | null)?.closest(
          'a[target="_blank"]',
        ) as HTMLAnchorElement | null
        if (!a) return
        e.preventDefault()
        const win = iframeRef.current?.contentWindow
        if (win) win.location.href = a.href
      },
      true,
    )
  }

  return (
    <div className="flex flex-col gap-3 h-[calc(100dvh-6.5rem)]">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold truncate" title={suiteLabel}>
            {suiteLabel}
          </h1>
          {run && (
            <Badge variant={run.status === 'failed' ? 'destructive' : 'production'}>
              {run.status}
            </Badge>
          )}
        </div>
        {run && (
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
            <span>{formatDateTime(run.startedAt, 'absoluteUTC')}</span>
            {typeof run.duration === 'number' && run.duration > 0 && (
              <>
                <span>·</span>
                <span>{formatDuration(run.duration)}</span>
              </>
            )}
          </div>
        )}
        {/* Back sits directly below the suite identity, mirroring the
            "Detailed report" placement on the run-detail page — actions
            live with the run header, not separately at the top. The
            Save-as-PDF icon pins to the right (`ml-auto`) for the same
            "download icon on the right" contract every other report
            page follows.

            The endpoint is gated to replay runs (returns 422 for
            discovery); we don't hide the icon up front because
            `useTestRun(runId)` is an in-memory progress lookup that
            returns null for completed runs (see #810 follow-ups for
            a persisted-run-meta endpoint). For now the button is
            always visible — clicking on a discovery run produces a
            clear inline error rather than a silently-missing
            affordance. Acceptable trade-off given discovery runs
            are rarely navigated to from this page. */}
        <div className="mt-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Back
          </Button>
          {runId && (
            <div className="ml-auto flex items-center gap-1">
              <DownloadPdfButton
                url={`/api/reports/run/${runId}/detailed/pdf`}
                title="Save full detailed report as PDF"
              />
              <EmailPdfButton
                url={`/api/reports/run/${runId}/detailed/pdf/email`}
                title="Email me this detailed report"
              />
            </div>
          )}
        </div>
      </div>
      <iframe
        ref={iframeRef}
        src={src}
        title="Playwright HTML report"
        className="w-full flex-1 border rounded-md bg-background"
        sandbox="allow-scripts allow-same-origin"
        onLoad={onLoad}
      />
    </div>
  )
}
